<template>
  <div class="multimap-menu">
    <v-autocomplete
      v-model="selectedMapId"
      :items="mapIds"
      :hint="createdAtHint"
      label="Map ID"
      dark
      dense
      rounded
      outlined
      persistent-hint
      item-text="alias"
      item-value="mapId"
      hide-detailse="auto"
      @focus="updateContentZIndex"
      @click="updateContentZIndex"
    />
    <v-speed-dial
      ref="speedDial"
      v-model="showMenu"
      :openOnHover="false"
      direction="right"
    >
      <template v-slot:activator>
        <v-btn
          v-model="showMenu"
          color="white"
          outlined
          rounded
          small
          :loading="loadingMaps"
        >
          {{ currentBaseLayer.name }}
        </v-btn>
      </template>
      <v-btn
        v-for="layer in baseLayers"
        :key="layer.name"
        class="ma-0 mb-2"
        color="white"
        outlined
        rounded
        small
        @click="changeBaseLayer(layer)"
      >
        {{ layer.name }}
      </v-btn>
    </v-speed-dial>
    <div>
      <v-btn
        v-for="overlayLayer in overlayLayers"
        :key="overlayLayer.name"
        :color="currentOverlayLayer.name === overlayLayer.name ? 'white' : 'grey'"
        class="ma-1 mt-2"
        outlined
        fab
        xSmall
        width="28"
        height="28"
        @click="changeOverlayLayer(overlayLayer)"
      >
        {{ overlayLayer.name }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import L from 'leaflet'
export default {
  props: {
    map: {
      type: Object,
      required: true
    }
  },
  data () {
    const selectedMapId = this.$route.query.map_id || null
    return {
      loadingMaps: false,
      mapIds: [],
      selectedMapId,
      maps: [],
      mapsConfigs: [],
      baseLayers: [],
      overlayLayers: [],
      currentBaseLayer: {
        name: this.$route.query.map_name || null,
        layer: null
      },
      currentOverlayLayer: {
        name: this.$route.query.map_type || null,
        layer: null
      },
      showMenu: false
    }
  },
  async mounted () {
    this.loadingIds = true
    const { data } = await this.$axios.get(this.$store.getters.projectPath + '/configs/multi-map')
    .catch(error => ({ data: { err: true, message: error.response.data } }))

    if (data.err) {
      this.$toasted.error(data.message)
    } else {
      this.mapIds = data
      if (this.selectedMapId) {
        await this.loadMaps()
      } else if (data.length) {
        this.selectedMapId = data[0].mapId
      }
    }
    this.loadingIds = false
    this.map.on('change-map', this.changeMap)
    this.map.on('change-type', this.changeType)
  },
  beforeDestroy () {
    this.baseLayers.forEach(val => {
      val.layer.remove()
    })

    this.overlayLayers.forEach(val => {
      val.layer.remove()
    })

    if (this.currentBaseLayer.layer) {
      this.currentBaseLayer.layer.remove()
    }

    if (this.currentOverlayLayer.layer) {
      this.currentOverlayLayer.layer.remove()
    }

    this.map.off('change-map', this.changeMap)
    this.map.off('change-type', this.changeType)
  },
  computed: {
    createdAtHint () {
      const val = this.mapIds.find(mapId => mapId.mapId === this.selectedMapId)
      if (val) {
        return val.createdAt
      } else {
        return ''
      }
    }
  },
  watch: {
    currentBaseLayer (val, prevVal) {
      if (prevVal.layer) {
        prevVal.layer.remove()
      }

      this.showBaseLayer(val)
    },
    currentOverlayLayer (val, prevVal) {
      if (prevVal.layer) {
        prevVal.layer.remove()
      }

      if (prevVal.rooms) {
        prevVal.rooms.remove()
      }

      this.showOverlayLayer(val)
    },
    showMenu (val) {
      val && this.changeMenuPostion()
    },
    async selectedMapId (val) {
      if (this.$route.query.map_id != val) {
        this.updateRouterQuery({ map_id: val })
      }

      val && await this.loadMaps()
    }
  },
  methods: {
    updateContentZIndex () {
      setTimeout(() => {
        const contents = document.querySelectorAll('.v-autocomplete__content')
        contents.forEach(content => {
          content.style.zIndex = "9999"
        })
      }, 100)
    },
    changeMenuPostion () {
      const nodes = [
        ...this.map._controlCorners.topleft.childNodes,
        ...this.map._controlCorners.bottomleft.childNodes
      ]
      let max = 0
      nodes.forEach(node => {
        max = Math.max(max, node.clientWidth)
      })

      const subMenu = this.$refs.speedDial.$el.querySelector('.v-speed-dial__list')
      if (subMenu) {
        subMenu.style.left = max + 'px'
      }
    },
    changeMap () {
      const currentMap = this.map.options.currentMap
      if (this.currentBaseLayer.name !== currentMap) {
        const baseLayer = this.baseLayers.find(val => val.name === currentMap)
        if (baseLayer) {
          this.currentBaseLayer = baseLayer
        } else {
          this.$toasted.error(`Can't find ${currentMap} map`)
        }
      }
    },
    changeType () {
      const currentMap = this.map.options.currentMap
      const currentType = this.map.options.currentType
      if (
        this.currentBaseLayer.name === currentMap &&
        this.currentOverlayLayer.name !== currentType
      ) {
        const overlayLayer = this.overlayLayers.find(val => val.name === currentType)
        if (overlayLayer) {
          this.currentOverlayLayer = overlayLayer
        } else {
          this.$toasted.error(`Can't find ${currentType} on the map ${currentMap}`)
        }
      }
    },
    async loadMaps () {
      this.map.options.currentMapId = this.selectedMapId
      this.map.fire('change-map-id')
      this.loadingMaps = true
      const { data } = await this.$axios.get(
        this.$store.getters.projectPath + '/configs/multi-map/' + this.selectedMapId,
        {
          params: {
            rooms: true
          }
        }
      ).catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.maps = data.reduce((maps, row) => {
          if (this.mapsConfigs.hasOwnProperty(row.name)) {
            this.mapsConfigs[row.name][row.type] = { ...row, matrix: JSON.parse(row.matrix) }
          } else {
            this.mapsConfigs[row.name] = { [row.type]: { ...row, matrix: JSON.parse(row.matrix) } }
          }

          if (row.type === 'background') {
            return maps
          }

          if (maps.hasOwnProperty(row.name)) {
            maps[row.name].push(row.type)
            maps[row.name] = maps[row.name].sort((a, b) => {
              if (a > b) return 1
              if (a < b) return -1
              return 0
            })
          } else {
            maps[row.name] = [row.type]
          }

          return maps
        }, {})

        this.map.options.maps = this.maps
        this.initLayers(this.maps)
      }
      this.loadingMaps = false
    },
    initLayers (maps) {
      this.baseLayers.forEach(row => {
        row.layer.remove()
      })

      this.overlayLayers.forEach(row => {
        row.layer.remove()

        if (row.rooms) {
          row.rooms.remove()
        }
      })

      const southWest = L.latLng(-256, 0),
        northEast = L.latLng(0, 256),
        bounds = L.latLngBounds(southWest, northEast)

      this.baseLayers = Object.keys(maps).map(mapName => {
        const layer = L.tileLayer(
          `${this.$store.getters.projectPath}/multi-map/images/${this.selectedMapId}/${mapName}/background/{z}/{x}x{y}.png?empty=true`, {
          bounds
        })

        return { layer, name: mapName }
      })
      this.initBaseLayer()
    },
    initBaseLayer () {
      const currentMap = this.$route.query.map_name || this.map.options.currentMap || null
      let baseLayer = this.baseLayers.find(val => val.name === currentMap)
      if (!baseLayer) {
        [ baseLayer ] = this.baseLayers
      }

      if (baseLayer) {
        this.currentBaseLayer = baseLayer
        this.initOverlayLayers(baseLayer)
      }
    },
    initOverlayLayers (baseLayer) {
      const currentMap = this.$route.query.map_name || this.map.options.currentMap || null
      const currentType = this.$route.query.map_type || this.map.options.currentType || null

      this.overlayLayers.forEach(row => {
        row.layer.remove()

        if (row.rooms) {
          row.rooms.remove()
        }
      })

      const southWest = L.latLng(-256, 0)
      const northEast = L.latLng(0, 256)
      const bounds = L.latLngBounds(southWest, northEast)

      let newOverlay = null
      this.overlayLayers = this.maps[baseLayer.name].map(overlay => {
        const layer = L.tileLayer(
          `${this.$store.getters.projectPath}/multi-map/images/${this.selectedMapId}/${baseLayer.name}/${overlay}/{z}/{x}x{y}.png`, {
          bounds
        })

        let rooms = null
        if (this.mapsConfigs[baseLayer.name][overlay].rooms) {
          const geoJsonRooms = this.mapsConfigs[baseLayer.name][overlay].rooms.map(room =>
            ({
              "type": "Feature",
              "properties": {
                "name": room.name,
                "area": room.area,
                "zoomOff": true
              },
              "geometry": {
                "type": "Polygon",
                "coordinates": [
                  room.coordinates
                ]
              }
            })
          )

          rooms = L.geoJson(geoJsonRooms, {
            style: {
              weight: 2,
              opacity: 0,
              color: '#FF5722',
              dashArray: '3',
              fillOpacity: 0
            },
            onEachFeature: this.roomInteraction
          })
        }

        if (
          overlay === currentType &&
          baseLayer.name === currentMap
        ) {
          newOverlay = { layer, name: overlay, rooms }
        } else if (parseInt(overlay) >= 0 && newOverlay === null) {
          newOverlay = { layer, name: overlay, rooms }
        }

        return { layer, name: overlay, rooms }
      })
      .sort((a,b) => {
        const aName = parseInt(a.name)
        const bName = parseInt(b.name)

        if (!isNaN(aName) && !isNaN(bName)) {
          return aName - bName
        }

        if (a.name > b.name) return 1
        if (a.name < b.name) return -1
        return 0
      })

      if (newOverlay) {
        this.currentOverlayLayer = newOverlay
      }
    },
    showBaseLayer ({ layer, name }) {
      this.map.options.currentMap = name
      if (this.$route.query.map_name != name) {
        this.updateRouterQuery({ map_name: name })
      }

      this.map.fire('change-map')
      this.map.addLayer(layer)
      this.map.flyTo([-128,128], 1)
      this.initOverlayLayers({ layer, name })
    },
    showOverlayLayer ({ layer, name, rooms }) {
      this.map.options.mapConfig = this.mapsConfigs[this.currentBaseLayer.name][this.currentOverlayLayer.name]
      this.map.options.currentType = name

      if (this.$route.query.map_type != name) {
        this.updateRouterQuery({ map_type: name })
      }

      this.map.fire('change-type')
      this.map.addLayer(layer)

      if (rooms) {
        this.map.addLayer(rooms)
        rooms.bringToBack()
      }
    },
    changeBaseLayer (data) {
      this.currentBaseLayer = data
    },
    changeOverlayLayer (data) {
      this.currentOverlayLayer = data
    },
    roomInteraction (feature, layer) {
      layer.on({
        mouseover: this.highlightRoom,
        mouseout: this.resetHighlightRoom
      })
    },
    highlightRoom (e) {
      const layer = e.target

      layer.setStyle({
        opacity: 1,
        fillOpacity: 0.3
      })

      let content = ''

      if (layer.feature.properties.name) {
        content += `<b>${layer.feature.properties.name}</b><br>`
      }

      content += `area = ${layer.feature.properties.area} m<sup>2</sup><br>`

      layer.bindTooltip(content, {
        sticky: true,
        opacity: 1,
        offset: L.point(15, 0)
      }).openTooltip()
    },
    resetHighlightRoom (e) {
      let layer = e.target

      layer.setStyle({
        opacity: 0,
        fillOpacity: 0
      })

      layer.closeTooltip()
    },
    updateRouterQuery (query) {
      this.$router.replace({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          ...query
        }
      })
    }
  }
}
</script>
